import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';

const FilterPresetsBootstrapContext = createContext(null);

export function FilterPresetsBootstrapProvider({ children }) {
  const [isBootstrapReady, setIsBootstrapReady] = useState(false);
  const [skipInitialListFetch, setSkipInitialListFetch] = useState(false);
  const clearActivePresetRef = useRef(null);
  const getActivePresetNameRef = useRef(null);
  const openSavePresetRef = useRef(null);
  /** Synced from preset components; cleared synchronously on reset before React state updates. */
  const selectedPresetNameRef = useRef(null);

  const markBootstrapReady = useCallback((options = {}) => {
    setSkipInitialListFetch(Boolean(options.skipInitialListFetch));
    setIsBootstrapReady(true);
  }, []);

  const resetBootstrap = useCallback(() => {
    setIsBootstrapReady(false);
    setSkipInitialListFetch(false);
  }, []);

  const registerPresetBridge = useCallback(({ clearActivePreset, getActivePresetName, openSavePreset }) => {
    clearActivePresetRef.current = clearActivePreset;
    getActivePresetNameRef.current = getActivePresetName;
    openSavePresetRef.current = openSavePreset || null;
  }, []);

  const syncSelectedPresetName = useCallback((name) => {
    selectedPresetNameRef.current = name || null;
  }, []);

  const clearActivePreset = useCallback(() => {
    selectedPresetNameRef.current = null;
    clearActivePresetRef.current?.();
  }, []);

  const getActivePresetName = useCallback(() => getActivePresetNameRef.current?.() ?? null, []);

  const getSelectedPresetName = useCallback(() => selectedPresetNameRef.current, []);

  const requestSavePreset = useCallback(() => {
    openSavePresetRef.current?.();
  }, []);

  useEffect(
    () => () => {
      clearActivePresetRef.current = null;
      getActivePresetNameRef.current = null;
      openSavePresetRef.current = null;
      selectedPresetNameRef.current = null;
      resetBootstrap();
    },
    [resetBootstrap]
  );

  const value = useMemo(
    () => ({
      isBootstrapReady,
      skipInitialListFetch,
      markBootstrapReady,
      resetBootstrap,
      registerPresetBridge,
      clearActivePreset,
      getActivePresetName,
      getSelectedPresetName,
      syncSelectedPresetName,
      requestSavePreset,
    }),
    [
      isBootstrapReady,
      skipInitialListFetch,
      markBootstrapReady,
      resetBootstrap,
      registerPresetBridge,
      clearActivePreset,
      getActivePresetName,
      getSelectedPresetName,
      syncSelectedPresetName,
      requestSavePreset,
    ]
  );

  return (
    <FilterPresetsBootstrapContext.Provider value={value}>
      {children}
    </FilterPresetsBootstrapContext.Provider>
  );
}

export function useFilterPresetsBootstrap() {
  const context = useContext(FilterPresetsBootstrapContext);
  if (!context) {
    throw new Error('useFilterPresetsBootstrap must be used within FilterPresetsBootstrapProvider');
  }
  return context;
}
